import React, { useEffect, useMemo, useState } from 'react';
import Button from './ui/Button';
import PeriodManager from '../services/PeriodManager';
import {
    createScheduleShare,
    updateScheduleShare,
    listScheduleShares,
    getInstitutionSettings,
    getDrafts
} from '../services/apiService';
import {
    buildCalendarWeekYearOptions,
    buildEndKeyOptionsForStart,
    getDefaultWeekKey,
    isValidEndForStart,
    parseYearWeekKey
} from '../utils/yearStructurePeriodUtils';

const ACTIVE_DRAFT_VALUE = '';

const toWeekKey = (year, week) => {
    if (year == null || week == null || Number.isNaN(Number(year)) || Number.isNaN(Number(week))) {
        return getDefaultWeekKey();
    }
    return `${Number(year)}|${Number(week)}`;
};

const getDraftPeriodId = (draft) => {
    const raw = draft?.period_id ?? draft?.periodId;
    if (raw === undefined || raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

const getDraftId = (draft) => {
    const raw = draft?.id;
    if (raw === undefined || raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

const getDraftName = (draft) => draft?.draft_name ?? draft?.draftName ?? draft?.name ?? 'Untitled draft';

const getPeriodActiveDraftId = (period) => {
    const raw = period?.active_schedule_draft_id ?? period?.activeScheduleDraftId;
    if (raw === undefined || raw === null || raw === '') return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
};

const formatWeekLabel = (year, week) => {
    if (year == null || week == null) return '—';
    return `${year} · Week ${week}`;
};

const ShareScheduleModal = ({
    isOpen,
    onClose,
    institutionUrlEndpoint,
    timePeriods = [],
    yearStructure = null,
    initialStartWeek,
    initialStartWeekYear,
    initialEndWeek,
    initialEndWeekYear
}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [share, setShare] = useState(null);
    const [endpoint, setEndpoint] = useState(institutionUrlEndpoint || '');
    const [drafts, setDrafts] = useState([]);
    const [copied, setCopied] = useState(false);
    const [celebrate, setCelebrate] = useState(false);

    const [startKey, setStartKey] = useState(() => toWeekKey(initialStartWeekYear, initialStartWeek));
    const [endKey, setEndKey] = useState(() => toWeekKey(
        initialEndWeekYear ?? initialStartWeekYear,
        initialEndWeek ?? initialStartWeek
    ));

    // periodId -> { enabled, scheduleDraftId: null | number }
    const [periodSelections, setPeriodSelections] = useState({});

    const calendarWeekYearOptions = useMemo(() => buildCalendarWeekYearOptions(), []);
    const endKeyOptionsForStart = useMemo(
        () => buildEndKeyOptionsForStart(startKey),
        [startKey]
    );

    useEffect(() => {
        if (!endKeyOptionsForStart.some((option) => option.value === endKey)) {
            setEndKey(startKey);
        }
    }, [startKey, endKey, endKeyOptionsForStart]);

    const { year: startWeekYear, week: startWeek } = useMemo(() => parseYearWeekKey(startKey), [startKey]);
    const { year: endWeekYear, week: endWeek } = useMemo(() => parseYearWeekKey(endKey), [endKey]);

    useEffect(() => {
        if (!isOpen) return;
        const nextStart = toWeekKey(initialStartWeekYear, initialStartWeek);
        let nextEnd = toWeekKey(
            initialEndWeekYear ?? initialStartWeekYear,
            initialEndWeek ?? initialStartWeek
        );
        const { year: sy, week: sw } = parseYearWeekKey(nextStart);
        const { year: ey, week: ew } = parseYearWeekKey(nextEnd);
        if (!isValidEndForStart(sy, sw, ey, ew)) {
            nextEnd = nextStart;
        }
        setStartKey(nextStart);
        setEndKey(nextEnd);
        setError(null);
        setShare(null);
        setCopied(false);
        setCelebrate(false);
    }, [isOpen, initialStartWeek, initialStartWeekYear, initialEndWeek, initialEndWeekYear]);

    useEffect(() => {
        let mounted = true;
        const ensureEndpoint = async () => {
            if (endpoint) return;
            try {
                const settings = await getInstitutionSettings();
                if (mounted && settings?.url_endpoint) {
                    setEndpoint(settings.url_endpoint);
                }
            } catch (_) { /* ignore */ }
        };
        if (isOpen) ensureEndpoint();
        return () => { mounted = false; };
    }, [isOpen, endpoint]);

    useEffect(() => {
        let mounted = true;
        const loadDrafts = async () => {
            if (!isOpen) return;
            try {
                const list = await getDrafts();
                if (mounted) setDrafts(Array.isArray(list) ? list : []);
            } catch (_) {
                if (mounted) setDrafts([]);
            }
        };
        loadDrafts();
        return () => { mounted = false; };
    }, [isOpen]);

    const overlappingPeriods = useMemo(() => (
        PeriodManager.getPeriodsOverlappingWeekRange(
            timePeriods,
            Number(startWeek),
            Number(startWeekYear),
            Number(endWeek),
            Number(endWeekYear),
            yearStructure
        )
    ), [timePeriods, startWeek, startWeekYear, endWeek, endWeekYear, yearStructure]);

    useEffect(() => {
        if (!isOpen) return;
        setPeriodSelections((prev) => {
            const next = {};
            overlappingPeriods.forEach((period) => {
                const id = Number(period.id);
                const existing = prev[id];
                next[id] = existing
                    ? { ...existing }
                    : { enabled: true, scheduleDraftId: null };
            });
            return next;
        });
    }, [isOpen, overlappingPeriods]);

    useEffect(() => {
        let mounted = true;
        const loadExisting = async () => {
            if (!isOpen) return;
            setLoading(true);
            const matchStart = toWeekKey(initialStartWeekYear, initialStartWeek);
            const matchEndRaw = toWeekKey(
                initialEndWeekYear ?? initialStartWeekYear,
                initialEndWeek ?? initialStartWeek
            );
            const { year: msy, week: msw } = parseYearWeekKey(matchStart);
            const { year: mey, week: mew } = parseYearWeekKey(matchEndRaw);
            const matchEnd = isValidEndForStart(msy, msw, mey, mew) ? matchEndRaw : matchStart;
            const matchStartParsed = parseYearWeekKey(matchStart);
            const matchEndParsed = parseYearWeekKey(matchEnd);
            try {
                const shares = await listScheduleShares();
                if (!mounted || !Array.isArray(shares) || shares.length === 0) {
                    if (mounted) setShare(null);
                    return;
                }
                // Only auto-select an active share that matches the current week range
                const match = shares.find((s) => (
                    s.isActive
                    && Number(s.startWeek) === Number(matchStartParsed.week)
                    && Number(s.startWeekYear) === Number(matchStartParsed.year)
                    && Number(s.endWeek) === Number(matchEndParsed.week)
                    && Number(s.endWeekYear) === Number(matchEndParsed.year)
                )) || null;

                if (!mounted) return;
                setShare(match);
                setCelebrate(false);

                if (match) {
                    const next = {};
                    (match.periods || []).forEach((row) => {
                        next[Number(row.periodId)] = {
                            enabled: !!row.enabled,
                            scheduleDraftId: row.scheduleDraftId ?? null
                        };
                    });
                    setPeriodSelections((prev) => ({ ...prev, ...next }));
                }
            } catch (_) {
                if (mounted) setShare(null);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        loadExisting();
        return () => { mounted = false; };
        // Only on open — avoid reloading when editing range locally
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const publicUrl = share?.slug && endpoint
        ? `${window.location.origin}/${endpoint}/s/${share.slug}`
        : '';

    const showSuccess = !!(share?.isActive && publicUrl);

    const draftsByPeriodId = useMemo(() => {
        const map = new Map();
        drafts.forEach((draft) => {
            const periodId = getDraftPeriodId(draft);
            if (periodId == null) return;
            if (!map.has(periodId)) map.set(periodId, []);
            map.get(periodId).push(draft);
        });
        return map;
    }, [drafts]);

    const periodsById = useMemo(() => {
        const map = new Map();
        timePeriods.forEach((period) => {
            map.set(Number(period.id), period);
        });
        overlappingPeriods.forEach((period) => {
            map.set(Number(period.id), period);
        });
        return map;
    }, [timePeriods, overlappingPeriods]);

    const sharedOverview = useMemo(() => {
        if (!share?.isActive) return [];
        const rows = (share.periods || []).filter((row) => row.enabled);
        return rows.map((row) => {
            const periodId = Number(row.periodId);
            const period = periodsById.get(periodId);
            const periodDrafts = draftsByPeriodId.get(periodId) || [];
            const pinnedId = row.scheduleDraftId == null ? null : Number(row.scheduleDraftId);
            const activeDraftId = getPeriodActiveDraftId(period);
            const draft = pinnedId != null
                ? periodDrafts.find((d) => getDraftId(d) === pinnedId)
                : periodDrafts.find((d) => getDraftId(d) === activeDraftId);
            const draftLabel = pinnedId == null
                ? (draft ? `Live — ${getDraftName(draft)}` : 'Activated (live) draft')
                : (draft ? getDraftName(draft) : `Draft #${pinnedId}`);

            return {
                periodId,
                name: period?.name || `Period ${periodId}`,
                color: period?.color || '#3B82F6',
                weekLabel: period
                    ? `W${period.start_week ?? period.startWeek}–W${period.end_week ?? period.endWeek}`
                    : null,
                draftLabel,
                isLive: pinnedId == null
            };
        });
    }, [share, periodsById, draftsByPeriodId]);

    const buildPeriodsPayload = () => (
        overlappingPeriods.map((period) => {
            const id = Number(period.id);
            const selection = periodSelections[id] || { enabled: false, scheduleDraftId: null };
            return {
                periodId: id,
                enabled: !!selection.enabled,
                scheduleDraftId: selection.scheduleDraftId || null
            };
        })
    );

    const handleCreate = async () => {
        setLoading(true);
        setError(null);
        try {
            const periods = buildPeriodsPayload();
            if (!periods.some((p) => p.enabled)) {
                throw new Error('Select at least one period to include');
            }

            const payload = {
                startWeek: Number(startWeek),
                startWeekYear: Number(startWeekYear),
                endWeek: Number(endWeek),
                endWeekYear: Number(endWeekYear),
                periods
            };

            const meta = await createScheduleShare(payload);
            setShare(meta);
            setCelebrate(true);
            setCopied(false);
        } catch (e) {
            setError(e.message || 'Failed to create share link');
        } finally {
            setLoading(false);
        }
    };

    const handleRevoke = async () => {
        if (!share?.id) return;
        setLoading(true);
        setError(null);
        try {
            await updateScheduleShare(share.id, { revoke: true });
            setShare(null);
            setCelebrate(false);
            setCopied(false);
        } catch (e) {
            setError(e.message || 'Failed to disable link');
        } finally {
            setLoading(false);
        }
    };

    const handleCopy = async () => {
        if (!publicUrl) return;
        try {
            await navigator.clipboard.writeText(publicUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 2000);
        } catch (_) { /* ignore */ }
    };

    const togglePeriod = (periodId) => {
        setPeriodSelections((prev) => {
            const current = prev[periodId] || { enabled: false, scheduleDraftId: null };
            return {
                ...prev,
                [periodId]: { ...current, enabled: !current.enabled }
            };
        });
    };

    const setPeriodDraft = (periodId, value) => {
        setPeriodSelections((prev) => {
            const current = prev[periodId] || { enabled: true, scheduleDraftId: null };
            return {
                ...prev,
                [periodId]: {
                    ...current,
                    scheduleDraftId: value === ACTIVE_DRAFT_VALUE ? null : Number(value)
                }
            };
        });
    };

    if (!isOpen) return null;

    const rangeLabel = startWeekYear === endWeekYear && startWeek === endWeek
        ? formatWeekLabel(startWeekYear, startWeek)
        : `${formatWeekLabel(startWeekYear, startWeek)} → ${formatWeekLabel(endWeekYear, endWeek)}`;

    const displayRangeLabel = showSuccess
        ? (
            share.startWeekYear === share.endWeekYear && share.startWeek === share.endWeek
                ? formatWeekLabel(share.startWeekYear, share.startWeek)
                : `${formatWeekLabel(share.startWeekYear, share.startWeek)} → ${formatWeekLabel(share.endWeekYear, share.endWeek)}`
        )
        : rangeLabel;

    return (
        <div className="fixed inset-0 bg-slate-900/45 backdrop-blur-[2px] flex items-center justify-center z-50 p-4">
            <style>{`
                @keyframes sharePop {
                    0% { transform: scale(0.55); opacity: 0; }
                    55% { transform: scale(1.08); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                @keyframes shareCheckDraw {
                    to { stroke-dashoffset: 0; }
                }
                @keyframes shareFadeUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes shareRing {
                    0% { transform: scale(0.7); opacity: 0.55; }
                    100% { transform: scale(1.55); opacity: 0; }
                }
                .share-success-icon {
                    animation: sharePop 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
                }
                .share-success-ring {
                    animation: shareRing 0.9s ease-out 0.1s both;
                }
                .share-success-check {
                    stroke-dasharray: 36;
                    stroke-dashoffset: 36;
                    animation: shareCheckDraw 0.45s ease-out 0.28s forwards;
                }
                .share-success-fade {
                    animation: shareFadeUp 0.45s ease-out both;
                }
                .share-success-fade-delay-1 { animation-delay: 0.12s; }
                .share-success-fade-delay-2 { animation-delay: 0.22s; }
                .share-success-fade-delay-3 { animation-delay: 0.32s; }
            `}</style>

            <div className="bg-white rounded-2xl shadow-2xl shadow-slate-900/15 w-full max-w-xl max-h-[90vh] flex flex-col border border-slate-200/80 overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-lg font-semibold text-slate-900 tracking-tight">
                            {showSuccess ? 'Shared link' : 'Share schedule'}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {showSuccess
                                ? 'Anyone with the link can view this schedule'
                                : 'Create a private link for a week range'}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-600 rounded-lg p-1 hover:bg-slate-100 transition-colors"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-5 space-y-4 overflow-y-auto">
                    {error && (
                        <div className="bg-red-50 text-red-700 text-sm rounded-xl p-3 border border-red-100">
                            {error}
                        </div>
                    )}

                    {showSuccess ? (
                        <div className="space-y-5">
                            <div className={`text-center pt-2 ${celebrate ? 'share-success-fade' : ''}`}>
                                <div className="relative inline-flex items-center justify-center mb-3">
                                    {celebrate && (
                                        <span className="absolute inset-0 rounded-full bg-emerald-400/30 share-success-ring" />
                                    )}
                                    <div
                                        className={`relative w-16 h-16 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 shadow-lg shadow-emerald-600/25 flex items-center justify-center ${celebrate ? 'share-success-icon' : ''}`}
                                    >
                                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path
                                                className={celebrate ? 'share-success-check' : ''}
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2.5}
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                </div>
                                <h3 className={`text-xl font-semibold text-slate-900 tracking-tight ${celebrate ? 'share-success-fade share-success-fade-delay-1' : ''}`}>
                                    {celebrate ? 'Link created' : 'Link ready'}
                                </h3>
                                <p className={`text-sm text-slate-500 mt-1 ${celebrate ? 'share-success-fade share-success-fade-delay-2' : ''}`}>
                                    Your schedule is available through a secure public link.
                                </p>
                            </div>

                            <div className={`rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-3 ${celebrate ? 'share-success-fade share-success-fade-delay-2' : ''}`}>
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3M5 11h14M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div className="min-w-0">
                                        <div className="text-[11px] uppercase tracking-wide font-medium text-slate-400">Week range</div>
                                        <div className="text-sm font-medium text-slate-800 mt-0.5">{displayRangeLabel}</div>
                                    </div>
                                </div>

                                <div className="border-t border-slate-200/80 pt-3">
                                    <div className="text-[11px] uppercase tracking-wide font-medium text-slate-400 mb-2">
                                        Included periods
                                    </div>
                                    {sharedOverview.length === 0 ? (
                                        <div className="text-sm text-slate-500">No periods included.</div>
                                    ) : (
                                        <ul className="space-y-2">
                                            {sharedOverview.map((item) => (
                                                <li
                                                    key={item.periodId}
                                                    className="flex items-start gap-2.5 rounded-lg bg-white border border-slate-200/90 px-3 py-2.5"
                                                >
                                                    <span
                                                        className="mt-1 inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                                                        style={{ backgroundColor: item.color }}
                                                    />
                                                    <div className="min-w-0 flex-1">
                                                        <div className="text-sm font-medium text-slate-900 truncate">
                                                            {item.name}
                                                        </div>
                                                        <div className="text-xs text-slate-500 mt-0.5">
                                                            {item.weekLabel ? `${item.weekLabel} · ` : ''}
                                                            {item.draftLabel}
                                                        </div>
                                                    </div>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className={`space-y-2 ${celebrate ? 'share-success-fade share-success-fade-delay-3' : ''}`}>
                                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-medium text-slate-400">
                                    <svg className="w-3.5 h-3.5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                    Public link
                                </div>
                                <div className="flex items-stretch gap-2">
                                    <div className="flex-1 min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700 font-mono truncate select-all">
                                        {publicUrl}
                                    </div>
                                    <Button
                                        onClick={handleCopy}
                                        variant={copied ? 'primary' : 'secondary'}
                                        className={copied ? '!bg-emerald-600 hover:!bg-emerald-700' : ''}
                                    >
                                        {copied ? 'Copied' : 'Copy'}
                                    </Button>
                                </div>
                                <a
                                    className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium"
                                    href={publicUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Open shared schedule
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5">
                                <div className="text-sm font-medium text-slate-800 mb-2">Week range</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 mb-1">Start</label>
                                        <select
                                            value={startKey}
                                            onChange={(e) => {
                                                const nextStart = e.target.value;
                                                setStartKey(nextStart);
                                                setEndKey((prevEnd) => {
                                                    const { year: sy, week: sw } = parseYearWeekKey(nextStart);
                                                    const { year: ey, week: ew } = parseYearWeekKey(prevEnd);
                                                    if (!sy || sw == null || Number.isNaN(sw)
                                                        || !ey || ew == null || Number.isNaN(ew)
                                                        || !isValidEndForStart(sy, sw, ey, ew)) {
                                                        return nextStart;
                                                    }
                                                    return prevEnd;
                                                });
                                            }}
                                            className="w-full px-2.5 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm bg-white"
                                        >
                                            {calendarWeekYearOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-slate-500 mb-1">End</label>
                                        <select
                                            value={endKey}
                                            onChange={(e) => setEndKey(e.target.value)}
                                            className="w-full px-2.5 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm bg-white"
                                        >
                                            {endKeyOptionsForStart.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <div className="text-sm font-medium text-slate-800 mb-1">Periods to include</div>
                                <p className="text-xs text-slate-500 mb-2.5">
                                    Choose which overlapping periods appear. Optionally pin a draft; otherwise the activated draft is used.
                                </p>

                                {overlappingPeriods.length === 0 ? (
                                    <div className="text-sm text-slate-500 border border-slate-200 rounded-xl p-3.5 bg-slate-50">
                                        No periods overlap this week range.
                                    </div>
                                ) : (
                                    <ul className="space-y-2">
                                        {overlappingPeriods.map((period) => {
                                            const periodId = Number(period.id);
                                            const selection = periodSelections[periodId] || { enabled: false, scheduleDraftId: null };
                                            const periodDrafts = draftsByPeriodId.get(periodId) || [];
                                            const activeDraftId = getPeriodActiveDraftId(period);
                                            const activeDraft = periodDrafts.find((d) => getDraftId(d) === activeDraftId);

                                            return (
                                                <li
                                                    key={periodId}
                                                    className={`border rounded-xl p-3 transition-colors ${selection.enabled ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-white'}`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <input
                                                            type="checkbox"
                                                            className="mt-1 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                                            checked={!!selection.enabled}
                                                            onChange={() => togglePeriod(periodId)}
                                                            aria-label={`Include period ${period.name}`}
                                                        />
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span
                                                                    className="inline-block w-3 h-3 rounded-sm shrink-0"
                                                                    style={{ backgroundColor: period.color || '#3B82F6' }}
                                                                />
                                                                <span className="text-sm font-medium text-slate-900 truncate">{period.name}</span>
                                                            </div>
                                                            <div className="text-xs text-slate-500 mt-0.5">
                                                                W{period.start_week ?? period.startWeek}–W{period.end_week ?? period.endWeek}
                                                                {activeDraft ? ` · Live: ${getDraftName(activeDraft)}` : ' · No activated draft'}
                                                            </div>
                                                            {selection.enabled && (
                                                                <label className="block mt-2 text-xs text-slate-600">
                                                                    Draft to share
                                                                    <select
                                                                        className="mt-1 w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                                                                        value={selection.scheduleDraftId == null ? ACTIVE_DRAFT_VALUE : String(selection.scheduleDraftId)}
                                                                        onChange={(e) => setPeriodDraft(periodId, e.target.value)}
                                                                    >
                                                                        <option value={ACTIVE_DRAFT_VALUE}>
                                                                            Activated (live){activeDraft ? ` — ${getDraftName(activeDraft)}` : ''}
                                                                        </option>
                                                                        {periodDrafts.map((draft) => {
                                                                            const id = getDraftId(draft);
                                                                            if (id == null) return null;
                                                                            return (
                                                                                <option key={id} value={String(id)}>
                                                                                    {getDraftName(draft)}{id === activeDraftId ? ' (activated)' : ''}
                                                                                </option>
                                                                            );
                                                                        })}
                                                                    </select>
                                                                </label>
                                                            )}
                                                        </div>
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            <div className="flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/60 px-3.5 py-3 text-xs text-slate-600">
                                <svg className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                                <span>
                                    The link is unlisted — only people you share it with can open it. You can disable it anytime.
                                </span>
                            </div>
                        </>
                    )}
                </div>

                <div className="px-5 py-4 border-t border-slate-100 flex flex-wrap gap-2 justify-between shrink-0 bg-slate-50/40">
                    {showSuccess ? (
                        <>
                            <Button onClick={handleRevoke} disabled={loading} variant="ghost" className="!text-red-600 hover:!bg-red-50">
                                Disable link
                            </Button>
                            <Button onClick={onClose} variant="primary">
                                Done
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={onClose} variant="secondary">Cancel</Button>
                            <Button
                                onClick={handleCreate}
                                disabled={loading || overlappingPeriods.length === 0}
                                variant="primary"
                            >
                                {loading ? 'Creating…' : 'Create public link'}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ShareScheduleModal;
